import { cameraMapStateAtom } from '@/atoms/viewer.atom';
import { project } from '@/core';
import { Check } from '@mynaui/icons-react';
import { useAtomValue } from 'jotai';
import { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

interface CoordinateInfoBoxProps {
  projectId: string;
  srid: number | null | undefined;
}

const CopyableCoord: FunctionComponent<{ label: string; value: string }> = ({ label, value }) => {
  const [copied, setCopied] = useState(false);
  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { t } = useTranslation('project');

  const handleCopy = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      navigator.clipboard.writeText(value);
      toast.success(t('viewer3d.coordsCopied'));
      setCopied(true);
      if (timeout.current) clearTimeout(timeout.current);
      timeout.current = setTimeout(() => setCopied(false), 1500);
    },
    [value, t],
  );

  return (
    <span
      onClick={handleCopy}
      className='cursor-copy hover:bg-white/10 rounded px-0.5 transition-colors inline-flex flex-wrap items-center justify-end gap-x-0.5'
      title={t('viewer3d.clickToCopyCoords')}>
      <span className='opacity-50 whitespace-nowrap'>{label}:</span>
      {/* One span per axis so a narrow corner wraps between axes, never inside a number. */}
      {value.split(', ').map((part, i, parts) => (
        <span key={i} className='font-mono whitespace-nowrap'>
          {part}
          {i < parts.length - 1 ? ',' : ''}
        </span>
      ))}
      {copied && <Check className='size-2.5 text-green-400' />}
    </span>
  );
};

const CoordinateInfoBox: FunctionComponent<CoordinateInfoBoxProps> = ({ projectId, srid }) => {
  const { t } = useTranslation('project');
  const cameraState = useAtomValue(cameraMapStateAtom(projectId));
  const [pos3d, setPos3d] = useState(cameraState.position);
  const [wgs84, setWgs84] = useState<[number, number, number] | null>(null);
  const hasValidSrid = srid != null && srid !== -1;

  // Throttle UI updates to ~200ms so it doesn't flicker too fast
  const lastUpdateRef = useRef(0);
  useEffect(() => {
    const now = Date.now();
    if (now - lastUpdateRef.current < 200) {
      const timer = setTimeout(() => {
        lastUpdateRef.current = Date.now();
        setPos3d(cameraState.position);
      }, 200);
      return () => clearTimeout(timer);
    }
    lastUpdateRef.current = now;
    setPos3d(cameraState.position);
  }, [cameraState.position]);

  // Project to WGS84 when position changes
  useEffect(() => {
    if (!hasValidSrid) return setWgs84(null);
    let cancelled = false;
    project(pos3d, `EPSG:${srid}`).then((coords) => {
      if (!cancelled) setWgs84(coords);
    });
    return () => {
      cancelled = true;
    };
  }, [pos3d, srid, hasValidSrid]);

  // Centimetres are as fine as a survey position is ever read.
  const fmtCm = (n: number) => n.toFixed(2);
  const fmt6 = (n: number) => n.toFixed(6);

  const coordStr = `${fmtCm(pos3d[0])}, ${fmtCm(pos3d[1])}, ${fmtCm(pos3d[2])}`;
  const wgs84Str = wgs84 ? `${fmt6(wgs84[1])}, ${fmt6(wgs84[0])}` : null;

  return (
    // Wraps part by part (EPSG / local / WGS84) when the corner is narrower than
    // one line — survey coordinates plus WGS84 run past a phone's width.
    <div className='bg-black/60 backdrop-blur-sm text-white/80 text-[9px] rounded px-1.5 py-0.5 pointer-events-auto select-none flex max-w-full flex-wrap items-center justify-end gap-x-2 gap-y-0.5'>
      {hasValidSrid && <span className='opacity-50 whitespace-nowrap'>EPSG:{srid}</span>}
      <CopyableCoord label={t('viewer3d.coordsLocal')} value={coordStr} />
      {wgs84Str && <CopyableCoord label='WGS84' value={wgs84Str} />}
    </div>
  );
};

export default CoordinateInfoBox;
